/**
 * Install Prompt Component
 * Shows an "Add to Home Screen" prompt for mobile users
 */
import React, { useState } from 'react';
import { usePWA } from '@/hooks/usePWA';

interface InstallPromptProps {
  /** Whether to show the prompt */
  show?: boolean;
  /** Called when user dismisses the prompt */
  onDismiss?: () => void;
}

const DISMISS_STORAGE_KEY = 'sabibooks.installPrompt.dismissed';

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    window.sessionStorage.setItem(DISMISS_STORAGE_KEY, '1');
  } catch {
    // sessionStorage may be unavailable (private mode, quota); ignore
  }
}

export function InstallPrompt({
  show = true,
  onDismiss,
}: InstallPromptProps): React.ReactElement | null {
  const { canInstall, isInstalled, promptInstall } = usePWA();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  // Don't show if already installed, can't install, explicitly hidden,
  // or dismissed in this browser session
  if (!show || isInstalled || !canInstall || dismissed) {
    return null;
  }

  const handleInstall = async () => {
    try {
      await promptInstall();
    } catch {
      // User denied the OS-level install dialog, or the browser cancelled it.
      // usePWA logs the outcome internally; nothing more to do in the UI.
    }
  };

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div
      role="region"
      aria-label="Install SabiBooks"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 p-4 sm:bottom-4 sm:left-auto sm:right-4 sm:max-w-sm"
    >
      <div className="pointer-events-auto mx-auto w-full max-w-sm rounded-2xl bg-surface p-6 shadow-xl">
        {/* App icon */}
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sabi-teal to-sabi-teal-dark shadow-lg">
          <span className="text-2xl font-bold text-white">S</span>
        </div>

        {/* Title and description */}
        <h2 className="text-center text-lg font-semibold text-ink-black">
          Install SabiBooks
        </h2>
        <p className="mt-2 text-center text-sm text-ink-dark">
          Add SabiBooks to your home screen for quick access and offline use.
        </p>

        {/* Features list */}
        <ul className="mt-4 space-y-2">
          <li className="flex items-center gap-2 text-sm text-ink-dark">
            <svg
              className="h-4 w-4 flex-shrink-0 text-success"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Works offline
          </li>
          <li className="flex items-center gap-2 text-sm text-ink-dark">
            <svg
              className="h-4 w-4 flex-shrink-0 text-success"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Faster than browser
          </li>
          <li className="flex items-center gap-2 text-sm text-ink-dark">
            <svg
              className="h-4 w-4 flex-shrink-0 text-success"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Home screen icon
          </li>
        </ul>

        {/* Action buttons */}
        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleInstall}
            className="w-full rounded-xl bg-sabi-teal py-3 text-base font-medium text-white transition-colors hover:bg-sabi-teal-dark focus:outline-none focus:ring-2 focus:ring-sabi-teal focus:ring-offset-2"
          >
            Install App
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-full rounded-xl py-3 text-base font-medium text-ink-dark transition-colors hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-ink-faint focus:ring-offset-2"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
